// @ts-nocheck
/**
 * ツインキーシステム - カテゴリマッピング
 *
 * スキル・パッシブ決定のためのカテゴリ分類を定義
 * 仕様書: twin_key_generic_skills_passives_v1.md
 */

// ========================================
// 役割カテゴリ（スキル用）
// ========================================

/**
 * 役割カテゴリ定義
 * スキル決定時の優先順位: 固有 → 役割 → 属性
 */
export const ROLE_CATEGORIES = {
  HEALER: 'healer',
  TANK: 'tank',
  SPEED: 'speed',
};

/**
 * 役割カテゴリに属するカテゴリ一覧
 * 【】は除去して判定する
 */
export const ROLE_CATEGORY_MAPPING = {
  [ROLE_CATEGORIES.HEALER]: [
    'スピリット',
    '天使',
    'エンジェル',
    'マーメイド',
  ],
  [ROLE_CATEGORIES.TANK]: [
    'ゴーレム',
    'ガーディアン',
    'マシン',
  ],
  [ROLE_CATEGORIES.SPEED]: [
    'インセクト',
    'インセクト・アリ',
    '鳥',
    '鳥・カラス',
    '鳥・カモメ',
    'フェニックス',
  ],
};

/**
 * 役割カテゴリの表示名
 */
export const ROLE_CATEGORY_NAMES = {
  [ROLE_CATEGORIES.HEALER]: 'ヒーラー',
  [ROLE_CATEGORIES.TANK]: 'タンク',
  [ROLE_CATEGORIES.SPEED]: 'スピード',
};

// ========================================
// カテゴリグループ（パッシブ用）
// ========================================

/**
 * カテゴリグループ定義
 * パッシブ決定に使用: 属性 × カテゴリグループ
 */
export const CATEGORY_GROUPS = {
  ATTACKER: 'attacker',
  TANK: 'tank',
  SPEED: 'speed',
  SUPPORT: 'support',
  BALANCE: 'balance',
  SPECIAL: 'special',
};

/**
 * カテゴリグループに属するカテゴリ一覧
 */
export const CATEGORY_GROUP_MAPPING = {
  [CATEGORY_GROUPS.ATTACKER]: [
    'ドラゴン',
    'ワイバーン',
    '蛮族',
    'デーモン',
  ],
  [CATEGORY_GROUPS.TANK]: [
    'ゴーレム',
    'ガーディアン',
    'マシン',
  ],
  [CATEGORY_GROUPS.SPEED]: [
    '鳥',
    '鳥・カラス',
    '鳥・カモメ',
    'フェニックス',
    'インセクト',
    'インセクト・アリ',
  ],
  [CATEGORY_GROUPS.SUPPORT]: [
    'スピリット',
    '天使',
    'エンジェル',
    'マーメイド',
  ],
  [CATEGORY_GROUPS.BALANCE]: [
    'ビースト',
    'ビースト・猫',
    'ビースト・狼',
    'ビースト・狸',
    'コア・ビースト',
    'ヒューマノイド',
    '魔導士',
    'ウィザード',
    'プリンセス',
    'スネーク',
  ],
  [CATEGORY_GROUPS.SPECIAL]: [
    'スライム',
    'プラント',
    'ゴースト',
    '魔族',
    'ダークドウェラ',
    'アビスソウル',
    'シャドウ',
    'リヴァイアサン',
  ],
};

/**
 * カテゴリグループの表示名
 */
export const CATEGORY_GROUP_NAMES = {
  [CATEGORY_GROUPS.ATTACKER]: 'アタッカー',
  [CATEGORY_GROUPS.TANK]: 'タンク',
  [CATEGORY_GROUPS.SPEED]: 'スピード',
  [CATEGORY_GROUPS.SUPPORT]: 'サポート',
  [CATEGORY_GROUPS.BALANCE]: 'バランス',
  [CATEGORY_GROUPS.SPECIAL]: '特殊',
};

/**
 * 複数カテゴリ持ちの場合の優先順位
 * 数値が小さいほど優先
 */
export const CATEGORY_GROUP_PRIORITY = {
  [CATEGORY_GROUPS.TANK]: 1,      // 防御重視
  [CATEGORY_GROUPS.SUPPORT]: 2,   // 回復重視
  [CATEGORY_GROUPS.SPEED]: 3,     // 速度重視
  [CATEGORY_GROUPS.ATTACKER]: 4,  // 攻撃重視
  [CATEGORY_GROUPS.SPECIAL]: 5,
  [CATEGORY_GROUPS.BALANCE]: 6,   // デフォルト
};

// ========================================
// コスト帯定義
// ========================================

/**
 * コスト帯定義
 * スキルセット選択に使用
 */
export const COST_BANDS = {
  LOW: 'low',     // コスト 1-3
  MID: 'mid',     // コスト 4-6
  HIGH: 'high',   // コスト 7+ (主に固有スキル持ち)
};

/**
 * コスト帯の範囲
 */
export const COST_BAND_RANGES = {
  [COST_BANDS.LOW]: { min: 1, max: 3 },
  [COST_BANDS.MID]: { min: 4, max: 6 },
  [COST_BANDS.HIGH]: { min: 7, max: Infinity },
};

// ========================================
// ヘルパー関数
// ========================================

/**
 * カテゴリ文字列を正規化
 * 【】を除去し、トリムする
 * @param {string} category - カテゴリ文字列
 * @returns {string} 正規化されたカテゴリ
 */
export const normalizeCategory = (category) => {
  if (!category) return '';
  return category.replace(/【|】/g, '').trim();
};

/**
 * カテゴリ文字列から複数カテゴリを抽出
 * 例: "【ドラゴン】【ビースト】" → ["ドラゴン", "ビースト"]
 * @param {string} categoryString - カテゴリ文字列
 * @returns {string[]} カテゴリ配列
 */
export const parseCategories = (categoryString) => {
  if (!categoryString) return [];

  // 文字列でない場合は空配列を返す
  if (typeof categoryString !== 'string') {
    console.warn('[parseCategories] categoryString is not a string:', categoryString);
    return [];
  }

  // 【】で囲まれたカテゴリを抽出
  const matches = categoryString.match(/【([^】]+)】/g);
  if (!matches) {
    // 【】がない場合はそのまま配列に
    const normalized = normalizeCategory(categoryString);
    return normalized ? [normalized] : [];
  }

  return matches.map(m => m.replace(/【|】/g, '').trim());
};

/**
 * カテゴリから役割カテゴリを取得
 * @param {string} categoryString - カテゴリ文字列（【】含む可）
 * @returns {string|null} 役割カテゴリID、該当なしはnull
 */
export const getRoleCategory = (categoryString) => {
  const categories = parseCategories(categoryString);

  for (const category of categories) {
    for (const [roleId, roleCategories] of Object.entries(ROLE_CATEGORY_MAPPING)) {
      if (roleCategories.includes(category)) {
        return roleId;
      }
    }
  }

  return null;
};

/**
 * カテゴリからカテゴリグループを取得
 * 複数マッチする場合は優先順位で決定
 * @param {string} categoryString - カテゴリ文字列（【】含む可）
 * @returns {string} カテゴリグループID（デフォルト: BALANCE）
 */
export const getCategoryGroup = (categoryString) => {
  const categories = parseCategories(categoryString);

  if (categories.length === 0) {
    return CATEGORY_GROUPS.BALANCE;
  }

  // マッチするグループを収集
  const matchedGroups = new Set();

  for (const category of categories) {
    for (const [groupId, groupCategories] of Object.entries(CATEGORY_GROUP_MAPPING)) {
      if (groupCategories.includes(category)) {
        matchedGroups.add(groupId);
      }
    }
  }

  if (matchedGroups.size === 0) {
    return CATEGORY_GROUPS.BALANCE;
  }

  if (matchedGroups.size === 1) {
    return [...matchedGroups][0];
  }

  // 複数マッチの場合、優先順位で決定
  let bestGroup = CATEGORY_GROUPS.BALANCE;
  let bestPriority = CATEGORY_GROUP_PRIORITY[CATEGORY_GROUPS.BALANCE];

  for (const groupId of matchedGroups) {
    const priority = CATEGORY_GROUP_PRIORITY[groupId];
    if (priority < bestPriority) {
      bestGroup = groupId;
      bestPriority = priority;
    }
  }

  return bestGroup;
};

/**
 * コストからコスト帯を取得
 * @param {number} cost - カードコスト
 * @returns {string} コスト帯ID
 */
export const getCostBand = (cost) => {
  const numCost = parseInt(cost, 10) || 0;

  if (numCost <= 3) return COST_BANDS.LOW;
  if (numCost <= 6) return COST_BANDS.MID;
  return COST_BANDS.HIGH;
};

/**
 * カテゴリがタンク系かどうか判定
 * @param {string} categoryString - カテゴリ文字列
 * @returns {boolean}
 */
export const isTankCategory = (categoryString) => {
  return getCategoryGroup(categoryString) === CATEGORY_GROUPS.TANK;
};

/**
 * カテゴリがヒーラー系かどうか判定
 * @param {string} categoryString - カテゴリ文字列
 * @returns {boolean}
 */
export const isHealerCategory = (categoryString) => {
  return getRoleCategory(categoryString) === ROLE_CATEGORIES.HEALER;
};

/**
 * カテゴリがスピード系かどうか判定
 * @param {string} categoryString - カテゴリ文字列
 * @returns {boolean}
 */
export const isSpeedCategory = (categoryString) => {
  return getRoleCategory(categoryString) === ROLE_CATEGORIES.SPEED;
};

// ========================================
// エクスポート
// ========================================

export default {
  // 定数
  ROLE_CATEGORIES,
  ROLE_CATEGORY_MAPPING,
  ROLE_CATEGORY_NAMES,
  CATEGORY_GROUPS,
  CATEGORY_GROUP_MAPPING,
  CATEGORY_GROUP_NAMES,
  CATEGORY_GROUP_PRIORITY,
  COST_BANDS,
  COST_BAND_RANGES,
  // 関数
  normalizeCategory,
  parseCategories,
  getRoleCategory,
  getCategoryGroup,
  getCostBand,
  isTankCategory,
  isHealerCategory,
  isSpeedCategory,
};
